import LereService from '~/services/LereService';

import { downloadCsvFile } from '~/utils/csvDownload';

import { getTourOfferHref } from './formatters/toursPricingUtils';
import { LereToursPricingHistoryParams, LereToursPricingHistoryRecord } from './types';

const CSV_EXPORT_PAGE_SIZE = 1000;
const TOURS_HISTORY_CSV_HEADERS = [
  'suggestionId',
  'offerName',
  'offerId',
  'salesforceId',
  'departureName',
  'departureDate',
  'suggestedActionType',
  'appliedActionType',
  'distanceCategory',
  'status',
  'originalTourPrice',
  'suggestedPriceChangePercent',
  'suggestedAfterPrice',
  'appliedPriceChangePercent',
  'appliedAfterPrice',
  'totalPaxSold',
  'maxPax',
  'leadtimeDays',
  'actionedByUsername',
  'actionedByUserId',
  'actionedAt',
  'offerUrl',
] as const;

function toExportPercent(value: number): number {
  return Number((value * 100).toFixed(2));
}

function buildToursHistoryCsvRows(rows: Array<LereToursPricingHistoryRecord>): Array<Record<string, unknown>> {
  return rows.map((row) => ({
    suggestionId: row.suggestion_id,
    offerName: row.offer_name,
    offerId: row.offer_bk,
    salesforceId: row.offer_salesforce_bk,
    departureName: row.departure_name,
    departureDate: row.departure_date,
    suggestedActionType: row.suggested_action_type,
    appliedActionType: row.applied_action_type,
    distanceCategory: row.distance_category,
    status: row.status,
    originalTourPrice: row.original_tour_price,
    suggestedPriceChangePercent: toExportPercent(row.suggested_price_change_percent),
    suggestedAfterPrice: row.suggested_after_price,
    appliedPriceChangePercent: toExportPercent(row.applied_price_change_percent),
    appliedAfterPrice: row.applied_after_price,
    totalPaxSold: row.total_pax_sold,
    maxPax: row.max_pax,
    leadtimeDays: row.leadtime,
    actionedByUsername: row.created_by_username,
    actionedByUserId: row.created_by_user_id,
    actionedAt: row.created_at,
    offerUrl: getTourOfferHref(row.offer_bk, row.offer_url),
  }));
}

export function downloadToursPricingHistoryCsv(rows: Array<LereToursPricingHistoryRecord>): void {
  if (rows.length === 0) {
    return;
  }

  const filename = `rm-tours-change-log-${new Date().toISOString().split('T')[0]}.csv`;

  downloadCsvFile(buildToursHistoryCsvRows(rows), [...TOURS_HISTORY_CSV_HEADERS], filename);
}

async function getAllToursPricingHistoryRows(
  filters: Omit<LereToursPricingHistoryParams, 'page' | 'limit'>,
): Promise<Array<LereToursPricingHistoryRecord>> {
  const firstPage = await LereService.getToursPricingHistory({
    ...filters,
    page: 1,
    limit: CSV_EXPORT_PAGE_SIZE,
  });

  if (firstPage.total === 0) {
    return [];
  }

  const totalPages = Math.ceil(firstPage.total / CSV_EXPORT_PAGE_SIZE);

  if (totalPages === 1) {
    return firstPage.result;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      LereService.getToursPricingHistory({
        ...filters,
        page: index + 2,
        limit: CSV_EXPORT_PAGE_SIZE,
      }),
    ),
  );

  return [firstPage, ...remainingPages].flatMap((response) => response.result);
}

export async function exportToursPricingHistoryCsv(
  filters: Omit<LereToursPricingHistoryParams, 'page' | 'limit'>,
): Promise<void> {
  const rows = await getAllToursPricingHistoryRows(filters);
  downloadToursPricingHistoryCsv(rows);
}
